import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Leaf, Award } from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: Shield,
      title: '100% Pure & Authentic',
      description: 'Sourced directly from trusted farmers, ensuring premium quality and authenticity.'
    },
    {
      icon: Leaf,
      title: 'Natural & Organic',
      description: 'Naturally grown products without harmful chemicals or preservatives.'
    },
    {
      icon: Award,
      title: 'Premium Quality',
      description: 'Carefully selected and processed to maintain the highest standards of excellence.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1702896779536-1c44a8d3e390?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjcm9wcyUyMHNreXxlbnwwfHx8fDE3Njk3MDExNTF8MA&ixlib=rb-4.1.0&q=85"
            alt="Hind Harvest - Indian Agricultural Bounty"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-center h-full max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Discover the
              <span className="block text-saffron-400">Pure Essence of India</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Premium FMCG products from India's finest sources, delivered with trust and authenticity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-8 py-4 bg-saffron-600 text-white font-semibold rounded-lg hover:bg-saffron-700 transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                Explore Products
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Hind Harvest?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We bring you the finest products from India's heartland, combining tradition with quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-14 h-14 bg-saffron-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="text-saffron-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission & Vision</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At Hind Harvest, we are building a premier distribution network for FMCG products 
                predominantly found in India's unorganized sector, aiming to deliver an exceptional 
                user experience.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Starting with the world's most precious spice - saffron - we are committed to bringing 
                authentic, premium quality products directly from trusted sources to discerning customers.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our vision extends beyond saffron. We plan to expand our portfolio to include a curated 
                selection of FMCG products that represent the best of India's rich agricultural heritage.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-8 py-4 bg-saffron-600 text-white font-semibold rounded-lg hover:bg-saffron-700 transition-all duration-300 hover:shadow-lg hover:scale-105"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link
                  to="/products"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-saffron-600 text-saffron-600 font-semibold rounded-lg hover:bg-saffron-50 transition-all duration-300"
                >
                  View Our Products
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1750579909774-ff40e6e1ca62"
                alt="Woman harvesting saffron flowers from fields"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;